import { DoneAll } from "@mui/icons-material";
import { Box, Button, IconButton, Typography } from "@mui/material";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useDeleteNotificationMutation } from "src/api/notifications";
import { useContext, useState } from "react";
import AppContext from "src/AppContext";
import { Roles } from "@arelia-types/index";
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { CustomPalette } from "src/theme/palette";
import logoSrc from "@arelia-assets/ARELIA_horizontal.png";
import DeleteConfirmationDialog from "@arelia-components/ui-atoms/DeleteConfirmationDialog";
import { formatDate } from "@arelia-utils/helpers/dates";

export type NotificationType = {
  id: string,
  identityId: string,
  message: string,
  isRead: boolean,
  source: string,
  date: string,
  partitionKey: string,
  key: string,
};

type Props = {
  notification: NotificationType,
  markAsRead: (id: string) => void,
  handleEdit: (notification: NotificationType) => void,
}

export function Notification({ notification, markAsRead, handleEdit }: Props) {
  const [confirmationDialogOpened, setConfirmationDialogOpened] = useState(false);
  const deleteNotificationMutation = useDeleteNotificationMutation();
  const { account } = useContext(AppContext);

  const handleDialogClose = (isConfirmed: boolean) => {
    if (isConfirmed) {
      deleteNotificationMutation.mutate(notification.key);
    }
    setConfirmationDialogOpened(false);
  };

  return (
    <>
      <Box sx={styles.wrapper}>
        {notification.source === "Arelia"
          ?
          <Box sx={styles.title}>
            <img src={logoSrc} style={styles.titleLogo} alt="logo" />
            <Typography color="secondary" sx={styles.titleText}>
              {formatDate(notification.date, "MM/dd/yy")}
            </Typography>
          </Box>
          : <></>
        }
        <Typography variant="subtitle2" color="secondary">
          <div dangerouslySetInnerHTML={{ __html: notification.message }} />
        </Typography>
        <Box sx={styles.content}>
          <Button variant="text" startIcon={<DoneAll />} onClick={() => markAsRead(notification.id)}>
            Mark as read
          </Button>
          {
            account?.role === Roles.Admin && notification.key &&
            <Box sx={styles.adminActions}>
              <IconButton color="secondary" sx={styles.actionButton} onClick={() => setConfirmationDialogOpened(true)}>
                <DeleteOutlinedIcon />
              </IconButton>
              <IconButton color="secondary" sx={styles.actionButton} onClick={() => handleEdit(notification)}>
                <ModeEditOutlinedIcon />
              </IconButton>
            </Box>
          }
        </Box>
      </Box>
      {
        confirmationDialogOpened &&
        <DeleteConfirmationDialog
          onClose={handleDialogClose}
          message={<>Are you sure you want to delete? <br />This action cannot be undone and notification will be deleted for all users.</>}
        />
      }
    </>
  );
}

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "24px",
    paddingBottom: "24px",
    borderBottom: `1px solid ${CustomPalette.gray.light}`
  },
  title: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    justifyContent: "space-between"
  },
  titleLogo: {
    width: "190px",
    height: "40px"
  },
  titleText: {
    fontSize: "14px"
  },
  content: {
    display: "flex",
    justifyContent: "space-between"
  },
  adminActions: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  actionButton: {
    color: CustomPalette.gray.main
  }
}